import config from '../config/config';
import { normalizeDomain } from '../utils/brandingUtils';

import { getLocalStorageItem } from './LocalStorage';

/**
 * Extracts the first subdomain or configured branding domain.
 * @returns {string | null} The first subdomain if present, otherwise null.
 */
const getFirstSubdomain = (): string | null => {
  try {
    // ✅ 1. Check URL query param
    const parameters = new URLSearchParams(window.location.search);
    const domainFromUrl = normalizeDomain(parameters.get('domain'));

    if (domainFromUrl) {
      localStorage.setItem('domain', domainFromUrl); // optional cache
      return domainFromUrl;
    }

    // ✅ 2. Check localStorage
    const rawStorageDomain = getLocalStorageItem('domain');
    const storageDomain = normalizeDomain(rawStorageDomain);
    if (rawStorageDomain && rawStorageDomain !== storageDomain) {
      localStorage.setItem('domain', storageDomain);
    }
    if (storageDomain) {
      return storageDomain;
    }

    // ✅ 3. Extract from hostname
    const { hostname } = window.location;
    const parts = hostname.split('.');

    if (parts.length < 2 || (parts.length === 2 && parts[1] === 'localhost')) {
      const fallbackDomain =
        config?.domain ||
        (config?.branding && config.branding !== 'energic' ? config.branding : '') ||
        '';
      return fallbackDomain || '';
    }

    // 👉 handle runa-test → runa
    const subdomain = parts[0];

    if (subdomain.includes('-')) {
      return subdomain.split('-')[0]; // "runa-test" → "runa"
    }

    return subdomain;
  } catch {
    return '';
  }
};

/**
 * Returns a valid domain for branding/headers, or null if it's blocked/empty.
 * @returns Valid domain string for branding/headers, or null if blocked/empty.
 */
export const getValidDomain = (): string | null => {
  const domain = getFirstSubdomain() || '';
  const blocked = ['web-dev', 'web-test', 'web', 'localhost'];

  if (domain && !blocked.some((d) => domain.includes(d))) {
    return domain;
  }
  return null;
};

export default getFirstSubdomain;
